/**
 * 🔒 Undo a merge (decision 7500; T3098): what a merge records, how long it can
 * be undone, the three groups its window sorts the moved rows into, and which
 * rows go back with the record unlisted. The API's census and the window both
 * read these lists, so the two cannot disagree about what moves.
 */
import { ITEM_MERGE_HOLDERS, type ItemMergeHolder, type MergeTarget } from './mergeMap';

/** 🔒 A merge can be undone for ninety days from the instant it was made (U3b). */
export const UNDO_MERGE_DAYS = 90;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The instant a merge made at `mergedAt` stops being undoable, as ISO. */
export function undoMergeUntil(mergedAt: string | Date): string {
  const at = typeof mergedAt === 'string' ? Date.parse(mergedAt) : mergedAt.getTime();
  return new Date(at + UNDO_MERGE_DAYS * DAY_MS).toISOString();
}

/** Whether a merge made at `mergedAt` can still be undone at `now`. */
export function undoMergeOpen(mergedAt: string | Date, now: Date = new Date()): boolean {
  return now.getTime() < Date.parse(undoMergeUntil(mergedAt));
}

/**
 * 🔒 The one exception to the ninety days (decision 7550): an import Merge made
 * before merges of the import were recorded counts its days from the day that
 * decision was written, so the owner's merges on Run can be undone.
 */
export const IMPORT_MERGE_FOUND_FROM = '2026-10-25T00:00:00.000Z';

/** The instant a merge's ninety days count from: the merge, or for an import merge found from its staged row, no earlier than decision 7550. */
export function undoMergeFrom(mergedAt: string | Date, foundFromStagedRow: boolean): Date {
  const at = typeof mergedAt === 'string' ? new Date(mergedAt) : mergedAt;
  if (!foundFromStagedRow) return at;
  const from = new Date(IMPORT_MERGE_FOUND_FROM);
  return at.getTime() < from.getTime() ? from : at;
}

/** One field an import Merge wrote into the company picked, with its value before and after (decision 7550). */
export interface ImportMergeField {
  field: string;
  from: unknown;
  to: unknown;
}

/**
 * 🔒 What an import Merge records in `RecordMerge.mergedValues?` (decision
 * 7550): the Zoho contact's name and id, and each field it wrote. An import
 * merge found from its staged row records none of it.
 */
export interface ImportMergeValues {
  displayName: string;
  sourceId: string;
  wrote: ImportMergeField[];
}

/** The import's side of an import merge's window (decision 7550). */
export interface UndoMergeImport {
  /** 🔒 A Zoho contact merged into a company, or a Zoho item into an item. */
  kind: 'Contact' | 'Item';
  /** The Zoho contact's or item's id, which Refresh From Zoho reads. */
  sourceId: string;
  /** The fields the Merge wrote, which stay on the survivor; null on a merge found from its staged row, which kept none. */
  wrote: ImportMergeField[] | null;
  /** 🔒 The name the undo puts the survivor back to, where the Merge renamed it and the name before is known; else null (decision 7550, T3158). */
  renameTo: string | null;
  /** True where the Merge was recorded with no values and nothing kept the survivor's name before it. */
  nameUnknown: boolean;
}

/**
 * 🔒 The registered pairs that can name a Company or a Person (decision 7500):
 * the Item merge's list, and the pairs a company or person merge moves by hand.
 * The census reads each before and after the merge, so a moved pair is recorded.
 */
export const MERGE_PAIR_HOLDERS: readonly ItemMergeHolder[] = [
  ...ITEM_MERGE_HOLDERS,
  { table: 'RecordAttribute', typeColumn: 'taggedRecordType', idColumn: 'taggedRecordId' },
  { table: 'RecordAttribute', typeColumn: 'referenceRecordType', idColumn: 'referenceRecordId' },
  { table: 'ValidationWarningAcknowledgement', typeColumn: 'taggedRecordType', idColumn: 'taggedRecordId' },
  { table: 'RecordCheck', typeColumn: 'recordType', idColumn: 'recordId' },
  { table: 'MessageLog', typeColumn: 'relatedRecordType', idColumn: 'relatedRecordId' },
  { table: 'AccountSuppression', typeColumn: 'targetType', idColumn: 'targetId' },
  { table: 'Message', typeColumn: 'suggestedRecordType', idColumn: 'suggestedRecordId' },
];

/** The pairs a merge of this record type is censused through. */
export function mergePairHolders(target: MergeTarget): readonly ItemMergeHolder[] {
  return target === 'Item' ? ITEM_MERGE_HOLDERS : MERGE_PAIR_HOLDERS;
}

/** How a listed row reads in the window: its kind word, which says what it is. */
export type UndoMergeRowKind = 'document' | 'deal' | 'person' | 'job' | 'site' | 'file' | 'note' | 'event' | 'line' | 'stock' | 'supplier' | 'price';

/**
 * 🔒 The tables whose rows the window lists, per record type, each with its kind
 * (decision 7500). Every other row the merge touched is the record's own fact
 * or a column naming who did something, and goes back with the record unlisted.
 */
export const UNDO_MERGE_LISTED: Readonly<Record<MergeTarget, Readonly<Record<string, UndoMergeRowKind>>>> = {
  CompanyRecord: {
    Quote: 'document', SalesOrder: 'document', Invoice: 'document', CreditNote: 'document', WorkOrder: 'document',
    PurchaseOrder: 'document', Bill: 'document', VendorCredit: 'document', Payment: 'document', Shipment: 'document',
    Package: 'document', Statement: 'document', Agreement: 'document',
    Deal: 'deal', Lead: 'deal',
    EmploymentRecord: 'person', SiteRecord: 'site',
    File: 'file', FileLink: 'file', Note: 'note', NoteRecordLink: 'note', Event: 'event',
  },
  PersonRecord: {
    QuoteRecipient: 'document', WorkOrder: 'document',
    Lead: 'deal', DealAuthority: 'deal',
    EmploymentRecord: 'job', EventPersonLink: 'event',
    File: 'file', FileLink: 'file', Note: 'note', NoteRecordLink: 'note', Event: 'event',
  },
  Item: {
    ItemTableLine: 'line', WOMaterialLine: 'line', WOPlannedPart: 'line', JobTypePart: 'line', ShipmentLine: 'line',
    PackageLine: 'line', ExpenseLine: 'line', BOMLine: 'line',
    StockMovement: 'stock', StockAllocation: 'stock', StockTransferLine: 'stock', StockCountLine: 'stock',
    ItemSupplier: 'supplier', PriceSheetRule: 'price', StockRevaluation: 'price',
    File: 'file', FileLink: 'file', Note: 'note', NoteRecordLink: 'note', Event: 'event',
  },
};

/**
 * 🔒 A column naming who did something, such as who wrote a note or sent a
 * statement (decision 7500). It goes back with the person unlisted: only that
 * person did it, and the merge recorded who it was.
 */
export function isActorColumn(column: string): boolean {
  return /ByPersonId$/.test(column) || column === 'authorPersonId' || column === 'actorPersonId' || column === 'ownerPersonId' || column === 'audiencePersonId';
}

/** The kind a recorded row lists under, or null where it goes back unlisted. */
export function undoMergeKindOf(target: MergeTarget, table: string, column: string): UndoMergeRowKind | null {
  if (target === 'PersonRecord' && isActorColumn(column)) return null;
  return UNDO_MERGE_LISTED[target][table] ?? null;
}

/** The three groups of the window (U1b): ticked where the search ties the row to the merged record. */
export type UndoMergeGroup = 'belonged' | 'madeAfter' | 'cantTell';

export const UNDO_MERGE_GROUPS: readonly UndoMergeGroup[] = ['belonged', 'madeAfter', 'cantTell'];

/** A row's key in the window and the press: its table and its id. */
export const undoMergeKey = (table: string, rowId: string): string => `${table}:${rowId}`;

/** One row the window lists. */
export interface UndoMergeRow {
  key: string;
  kind: UndoMergeRowKind;
  label: string;
  /** How the search placed it: addressed to the merged record, its Zoho record names it, nothing names it. */
  reason: string;
  group: UndoMergeGroup;
  /** Ticked when the window opens: Belonged to it alone. */
  ticked: boolean;
  /** The sentence that keeps the row where it is, its tick disabled; null where it can move. */
  fixed: string | null;
}

/**
 * 🔒 One job a person merge folded into the survivor's (decision 7960; T3177),
 * kept in `RecordMerge.mergedValues?` under `foldedJobs` before the fold ran:
 * the absorbed employment, its positions and its contact lines, every column of
 * each as `row_to_json` read them. Undo Merge writes them again on the restored
 * person with new ids.
 */
export interface MergeFoldedJob {
  employment: Record<string, unknown>;
  positions: Record<string, unknown>[];
  lines: Record<string, unknown>[];
}

/** The key a folded job's row carries in the window and the press, beside the tables a moved row's key names. */
export const UNDO_MERGE_FOLDED_TABLE = 'FoldedJob';

/** The folded jobs a person merge recorded beside the person's own columns; none on any other merge, or one made before decision 7960. */
export function foldedJobsOf(mergedValues: unknown): MergeFoldedJob[] {
  const list = (mergedValues as { foldedJobs?: unknown } | null)?.foldedJobs;
  if (!Array.isArray(list)) return [];
  return list.filter((one): one is MergeFoldedJob => typeof one === 'object' && one !== null && typeof (one as MergeFoldedJob).employment === 'object' && (one as MergeFoldedJob).employment !== null
    && Array.isArray((one as MergeFoldedJob).positions) && Array.isArray((one as MergeFoldedJob).lines));
}

/** One Zoho Books contact the survivor's import links name, which a merge made before decision 7500 may have carried onto it. */
export interface UndoMergeZohoChoice {
  /** The link row's own id, which the press names. */
  id: string;
  /** The Zoho contact's id. */
  sourceId: string;
  /** The contact's name as the newest read of Zoho spelled it, or its id where no read kept one. */
  name: string;
}

/**
 * 🔒 The Zoho references a merge that recorded nothing can give back (decision
 * 7500, the owner's case of 2026-10-24): every Zoho contact the survivor's
 * import links name, the ones the window has chosen for the returned record,
 * and whether the search could tell which one was the merged company's.
 */
export interface UndoMergeZoho {
  choices: UndoMergeZohoChoice[];
  chosen: string[];
  /** True where exactly one link names a contact the survivor's name does not, and one merge that recorded nothing went into it. */
  toldApart: boolean;
}

/** The window's read (decision 7500). */
export interface UndoMergePreview {
  mergeId: string;
  recordType: MergeTarget;
  /** The merged record's name as recorded, or the placeholder a merge made before records nothing. */
  mergedName: string;
  intoId: string;
  intoName: string;
  mergedAt: string;
  /** The instant the ninety days end. */
  until: string;
  open: boolean;
  /** False on a merge made before decision 7500: it recorded nothing, and the record returns empty. */
  recorded: boolean;
  rows: UndoMergeRow[];
  /** The record's own facts and the who-did-it columns that go back unlisted. */
  silent: number;
  /** Rows folded into or dropped beside the survivor's own, which stay. */
  joined: number;
  /** Rows deleted since the merge, or moved to a third record, which stay where they are. */
  gone: number;
  /** Why the merge cannot be undone at all, in words; null where it can. */
  refusal: string | null;
  /** 🔒 A company merge that recorded nothing: the survivor's Zoho references to give back, which the search reads by; null on every other merge. */
  zoho: UndoMergeZoho | null;
  /** 🔒 An import Merge (decision 7550): the contact and the fields it wrote; null on a merge made on the Duplicates report. */
  importMerge: UndoMergeImport | null;
}

/** One past merge on the Duplicates report's Merged view. */
export interface MergedRow {
  id: string;
  recordType: MergeTarget;
  mergedName: string;
  intoId: string;
  intoName: string;
  /** The survivor's page, or null where it is gone. */
  intoRoute: string | null;
  mergedAt: string;
  mergedBy: string;
  until: string;
  open: boolean;
  recorded: boolean;
  /** 🔒 A Zoho contact the import's Merge joined to the company (decision 7550). */
  byImport: boolean;
  /** 🔒 Decision 7880: a join to a company the same run created, named by its run, as *joined in import run 01a17577 as a second address*. */
  joinNote?: string;
}

export interface MergedListAnswer {
  merges: MergedRow[];
  /** How many of them can still be undone. */
  openCount: number;
}

/** The press's answer: the record that came back and how many rows went with it. */
export interface UndoMergeAnswer {
  recordType: MergeTarget;
  recordId: string;
  route: string;
  movedBack: number;
  /** 🔒 The Zoho contacts given back to the returned record, which Refresh from Zoho reads through the import; empty where none was. */
  refresh: string[];
  /** 🔒 What the ids in `refresh` are (decision 7550): Zoho contacts, the default, or a Zoho item an import merge gave back. */
  refreshKind?: 'Contact' | 'Item';
}

/** The page a record of this type opens on. */
export function mergeRecordRoute(target: MergeTarget, id: string): string {
  return `${target === 'CompanyRecord' ? '/companies' : target === 'PersonRecord' ? '/people' : '/items'}/${id}`;
}

/** 🔒 The Merged view's own address, opening one merge's window, which the import's Step through links to (U2b). */
export function undoMergeRoute(mergeId: string): string {
  return `/companies/duplicates?${new URLSearchParams({ view: 'merged', undo: mergeId }).toString()}`;
}
